import type { AppLocale, DeepPartialMessageTree, MessageTree } from "@airange/shared/i18n-common";
import { microLabCommonMessages } from "./common";
import { microLabDrawerMessages } from "./drawers";
import { microLabFormMessages } from "./forms";
import { microLabFeedbackMessages } from "./messages";
import { microLabNoticeMessages } from "./notices";
import { microLabPageMessages } from "./pages";

const MICRO_LAB_LOCALES = ["zh-CN", "en", "ja"] as const satisfies readonly AppLocale[];

const microLabCatalogSlices: Array<Record<AppLocale, DeepPartialMessageTree>> = [
  microLabCommonMessages,
  microLabPageMessages,
  microLabFormMessages,
  microLabDrawerMessages,
  microLabFeedbackMessages,
  microLabNoticeMessages,
];

function mergeMessageTrees(base: MessageTree, overrides?: DeepPartialMessageTree): MessageTree {
  if (!overrides) {
    return base;
  }
  const result: MessageTree = { ...base };
  for (const [key, overrideValue] of Object.entries(overrides)) {
    if (overrideValue === undefined) {
      continue;
    }
    const baseValue = result[key];
    if (typeof overrideValue === "string" || typeof baseValue === "string" || !baseValue) {
      result[key] = overrideValue as string | MessageTree;
      continue;
    }
    result[key] = mergeMessageTrees(baseValue, overrideValue as DeepPartialMessageTree);
  }
  return result;
}

function mergeCatalogSlices(locale: AppLocale): MessageTree {
  return microLabCatalogSlices.reduce<MessageTree>(
    (messages, slice) => mergeMessageTrees(messages, slice[locale]),
    {},
  );
}

const microLabBaseMessages = mergeCatalogSlices("zh-CN");

const microLabMessages = Object.fromEntries(
  MICRO_LAB_LOCALES.map((locale) => [
    locale,
    locale === "zh-CN"
      ? microLabBaseMessages
      : mergeMessageTrees(microLabBaseMessages, mergeCatalogSlices(locale)),
  ]),
) as Record<AppLocale, MessageTree>;

export function getMicroLabMessages(locale: AppLocale): MessageTree {
  return microLabMessages[locale];
}
